/**
 * The Monthly/Annual choice, shared by every surface that can start a checkout
 * (CLA-1622).
 *
 * ## Why one stored choice
 *
 * A clinician saw the cheaper annual price on the Settings plan card, pressed
 * Upgrade from the trial strip, and was only ever quoted monthly — the upgrade
 * panel hardcoded `interval: 'monthly'`. She started four Stripe checkouts in
 * 46 minutes. The plan card's own toggle was `useState('monthly')`, so it also
 * reset on every load, including the return from Stripe: a retry could
 * silently go back to monthly.
 *
 * So the choice lives here, once, and both the plan card and the upgrade panel
 * read and write it. It is persisted per user in localStorage, so it survives
 * navigation and the round trip to Stripe. Monthly is the default until
 * someone chooses; once chosen, it sticks.
 *
 * Storage can be refused (private mode), so every access is wrapped and an
 * in-memory copy keeps the choice consistent for the rest of the session.
 * Contents are a user id and the word "annual" or "monthly" — no PHI.
 */
import { useCallback, useSyncExternalStore } from 'react'
import { useAuthStore } from '@/stores/auth'

export type BillingInterval = 'monthly' | 'annual'

export const BILLING_INTERVAL_KEY = 'claro:billing-interval'

const memory = new Map<string, BillingInterval>()
const listeners = new Set<() => void>()

function keyFor(userId: number | string | null | undefined): string {
  return userId == null ? BILLING_INTERVAL_KEY : `${BILLING_INTERVAL_KEY}:${userId}`
}

function parse(value: string | null | undefined): BillingInterval | null {
  return value === 'annual' || value === 'monthly' ? value : null
}

export function readBillingInterval(userId?: number | string | null): BillingInterval {
  const key = keyFor(userId)
  try {
    const stored = parse(localStorage.getItem(key))
    if (stored) return stored
  } catch {
    /* storage unavailable — fall through to the in-memory copy */
  }
  return memory.get(key) ?? 'monthly'
}

export function writeBillingInterval(
  userId: number | string | null | undefined,
  interval: BillingInterval,
): void {
  const key = keyFor(userId)
  try {
    localStorage.setItem(key, interval)
    memory.delete(key)
  } catch {
    // Storage refused — the in-memory copy holds it for this session. Only
    // here, so a working store is always the single source of truth.
    memory.set(key, interval)
  }
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The signed-in user's billing interval and a setter. Every component using
 * this re-renders when any of them changes it, so the plan card behind an open
 * upgrade panel never disagrees with the panel.
 */
export function useBillingInterval(): [BillingInterval, (i: BillingInterval) => void] {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const interval = useSyncExternalStore(subscribe, () => readBillingInterval(userId))
  const set = useCallback((i: BillingInterval) => writeBillingInterval(userId, i), [userId])
  return [interval, set]
}
